// Pantalla de comedor (público por token, sin login) — patrón andón. Registro
// rápido de consumo: elige el servicio y marca el badge de cada comensal.
// Ruta /comedor/:token.
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ScanLine, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api/client';

interface Abrir { valido: boolean; kiosco?: string; fecha?: string; servicios?: string[] }
interface Reg { ok: boolean; motivo?: string; ya_estaba?: boolean; comensal?: { nombre: string; codigo: string }; total_servicio?: number }
const SRV: Record<string, string> = { desayuno: 'Desayuno', almuerzo_1: 'Almuerzo', almuerzo_2: 'Almuerzo 2', merienda: 'Merienda', cena: 'Cena', break: 'Break', dieta: 'Dieta' };

export function Comedor() {
  const { token = '' } = useParams();
  const [servicio, setServicio] = useState('');
  const [codigo, setCodigo] = useState('');
  const [last, setLast] = useState<{ tipo: 'ok' | 'dup' | 'err'; texto: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: abrir } = useQuery<Abrir>({
    queryKey: ['comedor', token],
    queryFn: async () => (await apiClient.post<Abrir>('/rpc/comedor_abrir', { _token: token })).data,
  });
  useEffect(() => { if (abrir?.servicios?.length && !servicio) setServicio(abrir.servicios[0]); }, [abrir, servicio]);

  const registrar = useMutation({
    mutationFn: async (cod: string) => (await apiClient.post<Reg>('/rpc/comedor_registrar', { _token: token, _codigo: cod, _servicio: servicio })).data,
    onSuccess: (r) => {
      if (!r.ok) setLast({ tipo: 'err', texto: 'Código no reconocido' });
      else if (r.ya_estaba) setLast({ tipo: 'dup', texto: `${r.comensal?.nombre} ya estaba registrado` });
      else setLast({ tipo: 'ok', texto: `${r.comensal?.nombre} · ${r.total_servicio} en el servicio` });
      setCodigo(''); inputRef.current?.focus();
    },
    onError: () => { setLast({ tipo: 'err', texto: 'Error al registrar' }); setCodigo(''); inputRef.current?.focus(); },
  });

  if (abrir && !abrir.valido) return <div className="flex min-h-screen items-center justify-center bg-slate-900 text-white">Comedor no válido</div>;

  const banner = last?.tipo === 'ok' ? 'bg-emerald-500' : last?.tipo === 'dup' ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="flex min-h-screen flex-col items-center bg-slate-900 p-6 text-white">
      <div className="flex w-full max-w-md flex-col items-center gap-5 pt-8">
        <ScanLine className="h-14 w-14 text-emerald-400" />
        <div className="text-center">
          <p className="text-sm text-slate-300">{abrir?.kiosco}</p>
          <h1 className="text-2xl font-semibold">Registro de comedor</h1>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {(abrir?.servicios ?? []).map((s) => (
            <button key={s} onClick={() => setServicio(s)}
              className={`rounded-full px-4 py-2 text-sm font-medium ${servicio === s ? 'bg-emerald-500' : 'bg-slate-700'}`}>{SRV[s] ?? s}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (codigo.trim() && servicio) registrar.mutate(codigo.trim()); }} className="flex w-full flex-col gap-3">
          <input ref={inputRef} autoFocus className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-4 text-center text-2xl tracking-widest placeholder:text-slate-500"
            placeholder="Marca tu badge" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
          <button type="submit" disabled={registrar.isPending || !servicio} className="rounded-lg bg-emerald-500 px-4 py-3 text-lg font-semibold hover:bg-emerald-600 disabled:opacity-50">
            {registrar.isPending ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : 'Registrar'}
          </button>
        </form>
        {last && (
          <div className={`flex w-full items-center gap-2 rounded-lg px-4 py-3 text-lg font-medium ${banner}`}>
            {last.tipo === 'ok' ? <Check className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />} {last.texto}
          </div>
        )}
      </div>
    </div>
  );
}
